import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconWrapper } from '../common/IconWrapper';
import { 
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram
} from 'react-icons/fa';

const socialLinks = [
  {
    name: 'Facebook',
    icon: FaFacebookF,
    url: 'https://facebook.com'
  },
  {
    name: 'Twitter',
    icon: FaTwitter,
    url: 'https://twitter.com'
  },
  {
    name: 'LinkedIn',
    icon: FaLinkedinIn,
    url: 'https://linkedin.com'
  },
  {
    name: 'Instagram',
    icon: FaInstagram,
    url: 'https://instagram.com'
  }
];

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.2fr_0.8fr_0.8fr_1.2fr] gap-8 md:gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center mb-8 group">
              <div className="relative">
                <div className="w-32 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 overflow-hidden bg-white border border-gray-200 px-2">
                  <img 
                    src="/logo.png" 
                    alt="FirstLogic Technologies" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </Link>
            <p className="text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
              Engineering Innovation with Intelligence. We are a global IT consulting and engineering company,
              driving digital transformation for enterprises worldwide.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
              Quick Links
            </h3>
            <ul className="space-y-4">
              {[
                { name: 'Services', path: '/services' },
                { name: 'Solutions', path: '/solutions' },
                { name: 'Case Studies', path: '/case-studies' },
                { name: 'Contact', path: '/contact' }
              ].map((item, index) => (
                <li key={index}>
                  <Link 
                    to={item.path} 
                    className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-300 transition-colors block py-2"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
              Resources
            </h3>
            <ul className="space-y-4">
              {[
                { name: 'Careers', path: '/careers' },
                { name: 'Privacy Policy', path: '/privacy-policy' },
                { name: 'Terms of Service', path: '/terms-of-service' }
              ].map((item, index) => (
                <li key={index}>
                  <Link 
                    to={item.path} 
                    className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-300 transition-colors block py-2"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
              Connect
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
              Follow us for the latest stories on engineering, innovation, and culture.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  aria-label={`Follow us on ${item.name}`}
                >
                  <IconWrapper icon={item.icon} className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800 text-center">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              &copy; {currentYear} FirstLogic Technologies. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Made with care for innovation.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};